import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BREAKPOINTS } from 'constants/grid';
import { useClientSideMediaQuery } from 'hooks/use-client-side-media-query';
import CarouselContext from 'components/Carousel/CarouselContext';
import { LegacyProductReference } from 'constants/products';
import { useIntersectionObserver } from 'hooks/use-intersection-observer';
import {
  trackShopRegularItemsCtaImpression,
  trackShopRegularItemsCtaClick,
} from './carousel-tracking';

export const useRecommendedCarousel = ({
  titleMobileAndTablet,
  titleDesktop,
  linkData,
  experience,
}: {
  products: LegacyProductReference[];
  titleMobileAndTablet: string;
  titleDesktop: string;
  linkData: { text: string; url: string; newWindow: boolean; type: 'link' | 'button' };
  experience?: string;
}) => {
  const carouselRef = useRef(null);
  const [carouselRendered, setCarouselRendered] = useState(null);
  const [isImpressionReported, setIsImpressionReported] = useState(false);
  const { isIntersecting } = useIntersectionObserver(carouselRendered);

  const isMobile = useClientSideMediaQuery({ maxWidth: BREAKPOINTS.lg });

  const title = isMobile ? titleMobileAndTablet : titleDesktop;

  const { productFindingMethod, setCarouselTitle, setExperience } = useContext(CarouselContext);

  setCarouselTitle(title);
  if (experience) setExperience(experience);

  useEffect(() => {
    setCarouselRendered(carouselRef.current);
  }, [carouselRef]);

  useEffect(() => {
    if (linkData?.url && isIntersecting && !isImpressionReported) {
      trackShopRegularItemsCtaImpression({
        title,
        linkUrl: linkData?.url,
        linkText: linkData?.text,
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsImpressionReported(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting, isImpressionReported]);

  const linkClick = useCallback(() => {
    if (linkData?.url) {
      trackShopRegularItemsCtaClick({
        title,
        linkUrl: linkData?.url,
        linkText: linkData?.text,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    title,
    productFindingMethod,
    linkClick,
    carouselRef,
  };
};
